// Path: ./src/app/pages/Database-driven-variables/BrowseCategoryMergeGroupsModal.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  ConfigurationResponseDto,
  UpdateConfigurationDto,
} from "@swagger/typescript-fetch-goflyer";

import { adminApiService } from "../../../utils/api";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useFlyerItemCategories } from "../../../hooks/useFlyerItemCategories";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import {
  BROWSE_CATEGORY_MERGE_GROUPS_KEY,
  MergeGroup,
  buildMergeGroupKey,
  createMergeGroup,
  findUnknownCategoryIds,
  hasMergeGroupErrors,
  parseMergeGroupsValue,
  toMergeGroupsPayload,
  uniqueMergeGroupKey,
  validateMergeGroups,
} from "../../../utils/browseCategoryMergeGroups";
import {
  AutocompleteControl,
  Field,
  Option,
  TextControl,
} from "../../components/FormKit/formControls";
import { token } from "../../components/FormKit/styled";

interface Props {
  open: boolean;
  fallbackRawValue: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const shortId = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

const unknownOption = (id: string): Option => ({
  label: `Unknown category (${shortId(id)})`,
  value: id,
});

/**
 * A group's `key` is generated from its card name, never typed. Once a group
 * has been saved the key is frozen (`generated: false`): the front-end uses it
 * for deep links and analytics, so renaming the card must not rename the key.
 */
interface GroupRow {
  group: MergeGroup;
  generated: boolean;
}

export const BrowseCategoryMergeGroupsModal = ({
  open,
  fallbackRawValue,
  onClose,
  onSaved,
}: Props) => {
  const authHeaders = useAuthHeaders();
  const { flyerItemCategories, fetchFlyerItemCategories } =
    useFlyerItemCategories();

  const [rows, setRows] = useState<GroupRow[]>([]);
  const [parseError, setParseError] = useState<string>();
  const [rawValue, setRawValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showJson, setShowJson] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      const [response] = await Promise.all([
        adminApiService.gfMiscellaneousAdminControllerGetConfigurationByKey(
          BROWSE_CATEGORY_MERGE_GROUPS_KEY,
          authHeaders,
        ),
        fetchFlyerItemCategories(),
      ]);

      if (cancelled) {
        return;
      }

      const live = (response as ConfigurationResponseDto | undefined)?.value;
      const source = live === undefined ? fallbackRawValue : live;
      const parsed = parseMergeGroupsValue(source);

      setRawValue(
        typeof source === "string" ? source : JSON.stringify(source, null, 2),
      );
      setRows(
        parsed.ok
          ? parsed.value.groups.map((group) => ({
              group,
              generated: !group.key,
            }))
          : [],
      );
      setParseError(parsed.ok ? undefined : parsed.error);
      setShowJson(false);
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const categoryById = useMemo(
    () =>
      new Map(
        (flyerItemCategories ?? []).map(
          (category) => [category.id, category] as const,
        ),
      ),
    [flyerItemCategories],
  );

  const allOptions = useMemo<Option[]>(
    () =>
      (flyerItemCategories ?? [])
        .map((category) => ({
          label: category.name || category.id,
          value: category.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [flyerItemCategories],
  );

  const groups = useMemo(() => rows.map((row) => row.group), [rows]);

  /** The key a not-yet-saved group gets: its card name plus its category names. */
  const generatedKey = useCallback(
    (group: MergeGroup) => {
      const orderedIds = [
        ...(group.primaryCategoryId ? [group.primaryCategoryId] : []),
        ...group.categoryIds.filter((id) => id !== group.primaryCategoryId),
      ];

      return buildMergeGroupKey(
        group.label ?? "",
        orderedIds.map((id) => categoryById.get(id)?.name ?? ""),
      );
    },
    [categoryById],
  );

  const withGeneratedKeys = useCallback(
    (next: GroupRow[]): GroupRow[] => {
      const taken = new Set(
        next
          .filter((row) => !row.generated)
          .map((row) => row.group.key)
          .filter(Boolean),
      );

      return next.map((row) => {
        if (!row.generated) {
          return row;
        }

        const key = uniqueMergeGroupKey(generatedKey(row.group), taken);

        if (key) {
          taken.add(key);
        }

        return key === row.group.key
          ? row
          : { ...row, group: { ...row.group, key } };
      });
    },
    [generatedKey],
  );

  useEffect(() => {
    setRows((previous) => {
      const next = withGeneratedKeys(previous);
      return next.some((row, index) => row !== previous[index])
        ? next
        : previous;
    });
  }, [withGeneratedKeys]);

  const mutateRows = (mutate: (previous: GroupRow[]) => GroupRow[]) =>
    setRows((previous) => withGeneratedKeys(mutate(previous)));

  const errors = useMemo(() => validateMergeGroups(groups), [groups]);

  const unknownCategoryIds = useMemo(
    () =>
      categoryById.size
        ? findUnknownCategoryIds(groups, new Set(categoryById.keys()))
        : [],
    [groups, categoryById],
  );

  const previewJson = useMemo(
    () => JSON.stringify(toMergeGroupsPayload(groups), null, 2),
    [groups],
  );

  const categoryLabel = useCallback(
    (id: string) => categoryById.get(id)?.name || unknownOption(id).label,
    [categoryById],
  );

  const optionsForGroup = useCallback(
    (group: MergeGroup, index: number): Option[] => {
      const takenElsewhere = new Set(
        groups.flatMap((other, otherIndex) =>
          otherIndex === index ? [] : other.categoryIds,
        ),
      );

      const available = allOptions.filter(
        (option) => !takenElsewhere.has(option.value),
      );

      const unknown = group.categoryIds
        .filter((id) => !categoryById.has(id))
        .map(unknownOption);

      return [...available, ...unknown];
    },
    [groups, allOptions, categoryById],
  );

  const updateGroup = (index: number, patch: Partial<MergeGroup>) =>
    mutateRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, group: { ...row.group, ...patch } }
          : row,
      ),
    );

  const handleCategoriesChange = (index: number, categoryIds: string[]) =>
    mutateRows((previous) =>
      previous.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }

        const primaryCategoryId = categoryIds.includes(
          row.group.primaryCategoryId,
        )
          ? row.group.primaryCategoryId
          : (categoryIds[0] ?? "");

        return {
          ...row,
          group: { ...row.group, categoryIds, primaryCategoryId },
        };
      }),
    );

  const handleAddGroup = () =>
    mutateRows((previous) => [
      ...previous,
      {
        group: createMergeGroup(previous.map((row) => row.group)),
        generated: true,
      },
    ]);

  const handleRemoveGroup = (index: number) =>
    mutateRows((previous) =>
      previous.filter((_row, rowIndex) => rowIndex !== index),
    );

  const handleSave = async () => {
    if (hasMergeGroupErrors(errors)) {
      toastError("Fix the highlighted fields before saving.");
      return;
    }

    setIsSaving(true);
    const body: UpdateConfigurationDto = {
      value: toMergeGroupsPayload(groups),
    };
    const result =
      await adminApiService.gfMiscellaneousAdminControllerUpdateConfiguration(
        body,
        BROWSE_CATEGORY_MERGE_GROUPS_KEY,
        authHeaders,
      );
    setIsSaving(false);

    if (!result) {
      return;
    }

    toastSuccess("Merged browse categories updated.");
    await onSaved();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="browse-category-merge-groups-title"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        id="browse-category-merge-groups-title"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pb: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
            Browse categories — merged cards
          </Typography>
          <Typography variant="caption" sx={{ color: token.color.textMuted }}>
            Group several categories into one card on the browse-category page.
            Pick categories by name — ids are filled in for you.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isSaving}
          aria-label="Close"
          sx={{ color: token.color.textMuted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: token.color.canvas, p: 2.5 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              py: 6,
            }}
          >
            <CircularProgress size={24} sx={{ color: token.color.accent }} />
            <Typography variant="body2" sx={{ color: token.color.textMuted }}>
              Loading configuration…
            </Typography>
          </Box>
        ) : (
          <>
            {parseError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  The saved value could not be opened in this editor
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  {parseError} Saving from here replaces it with the groups
                  below. Copy the current value first if you need it.
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    mb: 0,
                    p: 1,
                    bgcolor: "#fff",
                    border: `1px solid ${token.color.border}`,
                    borderRadius: "6px",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    maxHeight: 140,
                    overflow: "auto",
                  }}
                >
                  {rawValue}
                </Box>
              </Alert>
            )}

            {unknownCategoryIds.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {unknownCategoryIds.length} category id
                  {unknownCategoryIds.length > 1 ? "s do" : " does"} not match a
                  category in this environment
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  The front-end skips ids it does not recognise, so those deals
                  will not appear on the card. This usually means the value was
                  copied from another environment, or the category was deleted.
                </Typography>
              </Alert>
            )}

            {groups.length === 0 && !parseError && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
                No groups yet — every category gets its own card, exactly as the
                page looks today.
              </Alert>
            )}

            {rows.map(({ group, generated }, index) => {
              const groupErrors = errors[index] ?? {};
              const selectedOptions = optionsForGroup(group, index);
              const primaryIds =
                group.primaryCategoryId &&
                !group.categoryIds.includes(group.primaryCategoryId)
                  ? [group.primaryCategoryId, ...group.categoryIds]
                  : group.categoryIds;
              const primaryOptions = primaryIds.map((id) => ({
                label: categoryLabel(id),
                value: id,
              }));

              return (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: token.color.surface,
                    border: `1px solid ${token.color.border}`,
                    borderRadius: "12px",
                    opacity: group.enabled ? 1 : 0.72,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: "#1a1a1a" }}
                      >
                        {group.label ||
                          (group.primaryCategoryId
                            ? categoryLabel(group.primaryCategoryId)
                            : "New card")}
                      </Typography>
                      <Chip
                        label={`${group.categoryIds.length} categor${
                          group.categoryIds.length === 1 ? "y" : "ies"
                        }`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          bgcolor: token.color.accentSoft,
                          color: "#7a5230",
                        }}
                      />
                      <Tooltip
                        title={
                          generated
                            ? "Generated from the card name and its categories. It is fixed once this group is saved, because deep links and analytics use it."
                            : "Fixed — deep links and analytics use this key, so editing the card does not change it."
                        }
                        placement="top"
                        arrow
                      >
                        <Chip
                          label={group.key ? `key: ${group.key}` : "key: —"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontFamily: "monospace",
                            cursor: "help",
                            bgcolor: "#f3f4f6",
                            color: groupErrors.key
                              ? token.color.danger
                              : "#4b5563",
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip
                        title={
                          group.enabled
                            ? "Enabled — these categories share one card"
                            : "Disabled — these categories go back to separate cards"
                        }
                        placement="top"
                        arrow
                      >
                        <Switch
                          checked={group.enabled}
                          onChange={(event) =>
                            updateGroup(index, {
                              enabled: event.target.checked,
                            })
                          }
                          inputProps={{
                            "aria-label": `Enable group ${index + 1}`,
                          }}
                          sx={{
                            "& .Mui-checked": { color: token.color.accent },
                            "& .Mui-checked + .MuiSwitch-track": {
                              backgroundColor: token.color.accent,
                            },
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Remove group" placement="top" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveGroup(index)}
                          aria-label={`Remove group ${index + 1}`}
                          sx={{ color: token.color.danger }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {groupErrors.key && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 1.5,
                        color: token.color.danger,
                      }}
                    >
                      {groupErrors.key}
                    </Typography>
                  )}

                  <Field
                    id={`merge-group-${index}-categories`}
                    label="Categories on this card"
                    required
                    error={groupErrors.categoryIds}
                    help="Everything the card should show, including the primary. A category can belong to only one group."
                  >
                    <AutocompleteControl
                      id={`merge-group-${index}-categories`}
                      options={selectedOptions}
                      selected={group.categoryIds}
                      onSelectedChange={(values) =>
                        handleCategoriesChange(index, values)
                      }
                      placeholder="Search categories by name"
                      dataTestId={`merge-group-${index}-categories`}
                    />
                  </Field>

                  <Field
                    id={`merge-group-${index}-primary`}
                    label="Primary category"
                    required
                    error={groupErrors.primaryCategoryId}
                    help="The card borrows this category's name and image."
                  >
                    <AutocompleteControl
                      id={`merge-group-${index}-primary`}
                      multiple={false}
                      options={primaryOptions}
                      selected={
                        group.primaryCategoryId ? [group.primaryCategoryId] : []
                      }
                      onSelectedChange={(values) =>
                        updateGroup(index, {
                          primaryCategoryId: values[0] ?? "",
                        })
                      }
                      placeholder={
                        group.categoryIds.length
                          ? "Pick one of the categories above"
                          : "Pick the categories first"
                      }
                      dataTestId={`merge-group-${index}-primary`}
                    />
                  </Field>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, 1fr)",
                      },
                      gap: 2,
                    }}
                  >
                    {(
                      [
                        ["label", "Card name (English)"],
                        ["labelChinese", "Card name (Chinese)"],
                        ["labelFrench", "Card name (French)"],
                      ] as const
                    ).map(([field, label], fieldIndex) => {
                      const isPrimaryName = fieldIndex === 0;

                      return (
                        <Field
                          key={field}
                          id={`merge-group-${index}-${field}`}
                          label={label}
                          required={isPrimaryName}
                          error={isPrimaryName ? groupErrors.label : undefined}
                          help={
                            isPrimaryName
                              ? "Shown on the card. The key is generated from it and the categories."
                              : "Optional — falls back to the primary category's name."
                          }
                        >
                          <TextControl
                            id={`merge-group-${index}-${field}`}
                            value={group[field] ?? ""}
                            error={isPrimaryName && Boolean(groupErrors.label)}
                            placeholder={
                              isPrimaryName
                                ? "Name shown on the browse page"
                                : "Optional"
                            }
                            onChange={(event) =>
                              updateGroup(index, {
                                [field]: event.target.value || null,
                              } as Partial<MergeGroup>)
                            }
                          />
                        </Field>
                      );
                    })}
                  </Box>

                  <Box sx={{ maxWidth: 140 }}>
                    <Field
                      id={`merge-group-${index}-sort-order`}
                      label="Sort order"
                      error={groupErrors.sortOrder}
                      help="Lower first."
                    >
                      <TextControl
                        id={`merge-group-${index}-sort-order`}
                        type="number"
                        value={group.sortOrder ?? ""}
                        placeholder="10"
                        error={Boolean(groupErrors.sortOrder)}
                        onChange={(event) => {
                          const raw = event.target.value;
                          updateGroup(index, {
                            sortOrder: raw === "" ? null : Number(raw),
                          });
                        }}
                      />
                    </Field>
                  </Box>
                </Box>
              );
            })}

            <Button
              onClick={handleAddGroup}
              startIcon={<AddIcon />}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                borderColor: token.color.accent,
                color: token.color.accent,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: token.color.accentHover,
                  bgcolor: token.color.accentSoft,
                },
              }}
            >
              Add group
            </Button>

            <Box sx={{ mt: 2.5 }}>
              <Button
                onClick={() => setShowJson((previous) => !previous)}
                size="small"
                sx={{
                  textTransform: "none",
                  color: token.color.textMuted,
                  fontWeight: 600,
                }}
              >
                {showJson ? "Hide" : "Show"} the JSON that will be saved
              </Button>
              {showJson && (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 1,
                    p: 1.5,
                    bgcolor: token.color.surface,
                    border: `1px solid ${token.color.border}`,
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: "#1f2937",
                    maxHeight: 260,
                    overflow: "auto",
                  }}
                >
                  {previewJson}
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: token.color.textMuted,
            borderRadius: "10px",
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || isSaving || hasMergeGroupErrors(errors)}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : undefined
          }
          sx={{
            backgroundColor: token.color.accent,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: 3,
            boxShadow: "0 2px 8px rgba(245, 130, 32, 0.3)",
            "&:hover": { backgroundColor: token.color.accentHover },
          }}
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
